import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@hypevault/shared-types';

/**
 * Poll — v2 port of v1's poll.ejs.
 * Bar-chart vote tally. Votes are counted from chat messages matching
 * configured patterns (e.g. !vote 1, or just the number 1 if chatVoteByNumber).
 *
 * Config:
 *   question: string                      (required)
 *   options: string[]                     (required, 2–8 entries)
 *   accentColor: string                   (default '#a78bfa')
 *   durationSeconds: number               (optional — if set, shows countdown + auto-closes)
 *   command: string                       (default 'vote' — viewers type `!vote <n>`)
 *   chatVoteByNumber: boolean             (default true — bare "1"/"2" votes also count)
 *   oneVotePerViewer: boolean             (default true)
 *   showCounts: boolean                   (default true)
 */

interface VoteState {
  counts: number[];
  voters: Set<string>;
}

export function PollWidget({
  fields,
  latestChatMessage,
}: {
  fields: Record<string, unknown>;
  latestChatMessage: ChatMessage | null;
}) {
  const question = String(fields.question ?? '');
  const options = (fields.options as string[] | undefined) ?? [];
  const accent = String(fields.accentColor ?? '#a78bfa');
  const durationSeconds = fields.durationSeconds ? Number(fields.durationSeconds) : null;
  const command = String(fields.command ?? 'vote').toLowerCase();
  const chatVoteByNumber = (fields.chatVoteByNumber as boolean | undefined) ?? true;
  const oneVotePerViewer = (fields.oneVotePerViewer as boolean | undefined) ?? true;
  const showCounts = (fields.showCounts as boolean | undefined) ?? true;

  const [state, setState] = useState<VoteState>(() => ({
    counts: options.map(() => 0),
    voters: new Set(),
  }));
  const [startedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const lastId = useRef<string | null>(null);
  const closed = durationSeconds !== null && now - startedAt > durationSeconds * 1000;

  useEffect(() => {
    if (!durationSeconds) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [durationSeconds]);

  useEffect(() => {
    if (!latestChatMessage || latestChatMessage.id === lastId.current) return;
    lastId.current = latestChatMessage.id;
    if (closed) return;

    let optionIndex: number | null = null;
    if (latestChatMessage.command === command) {
      const arg = latestChatMessage.args[0];
      if (arg) {
        const n = Number(arg);
        if (Number.isInteger(n) && n >= 1 && n <= options.length) optionIndex = n - 1;
      }
    } else if (chatVoteByNumber) {
      const trimmed = latestChatMessage.message.trim();
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        if (Number.isInteger(n) && n >= 1 && n <= options.length) optionIndex = n - 1;
      }
    }
    if (optionIndex === null) return;

    const voterId = latestChatMessage.viewerId ?? latestChatMessage.viewerName;
    setState((prev) => {
      if (oneVotePerViewer && prev.voters.has(voterId)) return prev;
      const counts = [...prev.counts];
      counts[optionIndex] = (counts[optionIndex] ?? 0) + 1;
      const voters = new Set(prev.voters);
      voters.add(voterId);
      return { counts, voters };
    });
  }, [latestChatMessage, closed, command, chatVoteByNumber, options.length, oneVotePerViewer]);

  if (!question || options.length === 0) {
    return (
      <div
        style={{
          padding: '14px 20px',
          color: 'rgba(255,255,255,0.3)',
          fontFamily: "'Poppins', system-ui, sans-serif",
          fontSize: 12,
          background: 'rgba(15,12,30,0.85)',
          border: '1px solid rgba(139,92,246,0.18)',
          borderRadius: 10,
        }}
      >
        Set `question` and `options` to start a poll
      </div>
    );
  }

  const total = state.counts.reduce((a, b) => a + b, 0);
  const winnerIdx = state.counts.indexOf(Math.max(...state.counts));
  const remaining = durationSeconds
    ? Math.max(0, durationSeconds - Math.floor((now - startedAt) / 1000))
    : null;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: '14px 20px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 360,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: accent,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          📊 {closed ? 'Poll closed' : 'Vote now'}
        </div>
        {remaining !== null && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              color: remaining < 10 ? '#ef4444' : 'rgba(255,255,255,0.5)',
            }}
          >
            {remaining}s
          </div>
        )}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 12, lineHeight: 1.25 }}>
        {question}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((opt, i) => {
          const count = state.counts[i] ?? 0;
          const pct = total > 0 ? (count / total) * 100 : 0;
          const isWinner = closed && total > 0 && i === winnerIdx;
          return (
            <div key={i}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  fontSize: 12,
                  marginBottom: 3,
                }}
              >
                <span
                  style={{
                    fontWeight: 700,
                    color: isWinner ? accent : 'white',
                  }}
                >
                  <span style={{ color: accent, marginRight: 6 }}>{i + 1}.</span>
                  {opt}
                </span>
                {showCounts && (
                  <span
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: 'rgba(255,255,255,0.55)',
                      fontWeight: 700,
                    }}
                  >
                    {count} ({pct.toFixed(0)}%)
                  </span>
                )}
              </div>
              <div
                style={{
                  height: 8,
                  background: 'rgba(255,255,255,0.06)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: '100%',
                    background: isWinner
                      ? `linear-gradient(90deg, ${accent}, #fbbf24)`
                      : `linear-gradient(90deg, ${accent}, ${accent}aa)`,
                    transition: 'width 500ms cubic-bezier(0.22,1,0.36,1)',
                    borderRadius: 4,
                    boxShadow: `0 0 12px ${accent}66`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {total === 0 && !closed && (
        <div
          style={{
            marginTop: 12,
            fontSize: 11,
            color: 'rgba(255,255,255,0.4)',
            textAlign: 'center',
          }}
        >
          Type !{command} 1–{options.length} in chat to vote
        </div>
      )}
    </div>
  );
}
